import { skipToken } from "@reduxjs/toolkit/query"
import { GiftCard_Status } from "gen/giftcard/models_pb"
import { useState, useCallback } from "react"
import { Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import { logger } from "shared-ts/logger"
import { GIFT_CARD_NUMBER_LENGTH } from "shared-ts/utils/giftCardNumberInput"

import { getUserMessage, isApiError } from "features/api/apiSlice"
import {
  useGetGiftCardQuery,
  useLazyGetGiftCardByNumberQuery,
} from "features/api/giftcard"

export type GiftCardInputMode = "ACTIVE" | "CREATED" | "ALL"

export interface UseGiftCardInputProps {
  onGiftCardFound?: (giftCardId: UUID) => void
  onGiftCardCleared?: () => void
  mode: GiftCardInputMode
  existingGiftCardId?: UUID
  allowActivate?: boolean
}

export interface GiftCardDetails {
  balance: Money
  status: GiftCard_Status
}

export interface UseGiftCardInputResult {
  cardNumber: string
  setCardNumber: (value: string) => void
  giftCardDetails?: GiftCardDetails
  isLoadingBalance: boolean
  error?: string
  lookupBalance: () => Promise<void>
  giftCardId?: UUID
  giftCardStatus?: GiftCard_Status
}

export function useGiftCardInput({
  onGiftCardFound,
  onGiftCardCleared,
  mode,
  existingGiftCardId,
  allowActivate = false,
}: UseGiftCardInputProps): UseGiftCardInputResult {
  const [cardNumber, setCardNumber] = useState("")
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  )
  const [isUsingExisting, setIsUsingExisting] = useState(!!existingGiftCardId)

  // Fetch existing gift card data when editing
  const { data: existingData, isLoading: isLoadingExisting } =
    useGetGiftCardQuery(
      existingGiftCardId && isUsingExisting
        ? { id: existingGiftCardId }
        : skipToken
    )

  const [
    getGiftCardByNumber,
    { data: lookupData, error: queryError, isLoading: isLoadingLookup, reset },
  ] = useLazyGetGiftCardByNumberQuery()

  const giftCardData = isUsingExisting ? existingData : lookupData

  const displayCardNumber = isUsingExisting
    ? giftCardData?.giftCard?.cardNumberLast4 ?? ""
    : cardNumber

  const giftCardId = isUsingExisting
    ? existingGiftCardId
    : giftCardData?.giftCard?.id
      ? UUID.fromPB(giftCardData.giftCard.id)
      : undefined

  const pbBalance = giftCardData?.giftCardBalance?.balance
  const giftCardStatus = giftCardData?.giftCard?.status
  const giftCardDetails: GiftCardDetails | undefined =
    pbBalance && giftCardStatus != null
      ? { balance: Money.fromPB(pbBalance), status: giftCardStatus }
      : undefined

  const isLoadingBalance = isLoadingExisting || isLoadingLookup

  const error =
    validationError ??
    (queryError
      ? (isApiError(queryError) && getUserMessage(queryError)) ||
        "Gift card not found"
      : undefined)

  const clearBalance = useCallback(() => {
    reset()
    setValidationError(undefined)
    setIsUsingExisting(false)
    onGiftCardCleared?.()
  }, [reset, onGiftCardCleared])

  const handleCardNumberChange = useCallback(
    (value: string) => {
      setCardNumber(value)
      if (lookupData || queryError || validationError || isUsingExisting) {
        clearBalance()
      }
    },
    [clearBalance, lookupData, queryError, validationError, isUsingExisting]
  )

  const lookupBalance = useCallback(async () => {
    if (cardNumber.length !== GIFT_CARD_NUMBER_LENGTH) {
      setValidationError(
        `Gift card numbers must be ${GIFT_CARD_NUMBER_LENGTH} digits.`
      )
      return
    }

    try {
      const result = await getGiftCardByNumber({
        cardNumber,
      }).unwrap()

      // Validate status based on mode
      const status = result.giftCard?.status
      const statusError = validateGiftCardStatus(
        status,
        mode,
        allowActivate,
        result.giftCardBalance?.balance
          ? Money.fromPB(result.giftCardBalance.balance)
          : undefined
      )

      if (statusError) {
        setValidationError(statusError)
        reset()
        return
      }

      setValidationError(undefined)

      const giftCardUUID = result.giftCard?.id
        ? UUID.fromPB(result.giftCard.id)
        : undefined

      if (giftCardUUID) {
        onGiftCardFound?.(giftCardUUID)
      }
    } catch (error) {
      logger.error("Failed to lookup gift card", { error })
    }
  }, [
    cardNumber,
    getGiftCardByNumber,
    onGiftCardFound,
    mode,
    reset,
    allowActivate,
  ])

  return {
    cardNumber: displayCardNumber,
    setCardNumber: handleCardNumberChange,
    giftCardDetails,
    isLoadingBalance,
    error,
    lookupBalance,
    giftCardId,
    giftCardStatus: lookupData?.giftCard?.status,
  }
}

export function validateGiftCardStatus(
  status: GiftCard_Status | undefined,
  mode: GiftCardInputMode,
  allowActivate: boolean,
  balance?: Money
): string | undefined {
  if (status === undefined || status === GiftCard_Status.UNDEFINED) {
    return "Invalid gift card."
  }

  switch (mode) {
    case "ALL":
      // Accept any valid gift card status
      return undefined

    case "ACTIVE":
      switch (status) {
        case GiftCard_Status.ACTIVE:
          return undefined // Valid
        case GiftCard_Status.CREATED:
          return allowActivate
            ? undefined
            : "This gift card has not been activated yet."
        case GiftCard_Status.DEACTIVATED:
          return "This gift card has been deactivated."
        default:
          return "Invalid gift card."
      }

    case "CREATED":
      switch (status) {
        case GiftCard_Status.CREATED: {
          if (!balance) {
            return "Unable to look up gift card balance."
          }
          if (balance.isPositive()) {
            return "This gift card should have a $0 balance."
          }
          return undefined // Valid
        }
        case GiftCard_Status.ACTIVE:
          return "This gift card has already been activated."
        case GiftCard_Status.DEACTIVATED:
          return "This gift card cannot be reactivated."
        default:
          return "Invalid gift card."
      }
  }
}
